import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Modal, Button, Form } from 'react-bootstrap';
import Select from 'react-select';
import { createSparepartImport } from '../../../actions/sparepartImports';
import { getWarehouseOptions, getSparepartOptions } from '../../../actions/spare_part';
import { getSupplierOptions } from '../../../actions/material';
import { usePermission } from '../../../hooks/usePermission';
import SparepartImportItemForm from './SparepartImportItemForm';
import { Plus } from 'react-feather';
import useActiveFirm from '../../../hooks/useActiveFirm';

const SparepartImportCreateForm = ({
    show,
    handleClose,
    createSparepartImport,
    getSupplierOptions,
    getWarehouseOptions,
    getSparepartOptions,
    spare_parts = [],
    suppliers = [],
    warehouses = [],
    user
}) => {
    const { userCan } = usePermission();
    const { firmId, firmName } = useActiveFirm();

    if (!userCan('create sparepart imports')) {
        return null;
    }
    
    // Check if user is director or superadmin
    const isDirectorOrSuperAdmin = user?.roles?.some(role => 
        role.name === 'director' || role.name === 'superadmin'
    );

    const [formData, setFormData] = useState({
        supplier_id: '',
        firm_id: '',
        warehouse_id: '',
        date: new Date(),
        description: '',
        items: [{ spare_part_id: '', quantity: '', price: '' }]
    });

    // Add a formKey state to force re-render when warehouse changes
    const [formKey, setFormKey] = useState(Date.now());
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [selectedWarehouse, setSelectedWarehouse] = useState(null);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        getSupplierOptions();
        // Load warehouses when firm is available
        if (firmId) {
            getWarehouseOptions({ firm_id: firmId, type: 'part' });
        }
    }, [getSupplierOptions, getWarehouseOptions, firmId]);

    // Auto-populate with global firm when modal opens
    useEffect(() => {
        if (show && firmId) {
            setFormData(prev => ({ ...prev, firm_id: firmId }));
            getWarehouseOptions({ firm_id: firmId, type: 'part' });
            getSparepartOptions({ firm_id: firmId });
        }
    }, [show, firmId, getWarehouseOptions, getSparepartOptions]);

    const { date, description, items } = formData;

    const onChange = e => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const onItemChange = (index, field, value) => {
        const newItems = [...items];
        // Convert quantity and price to numbers with 2 decimal places
        if (field === 'quantity' || field === 'price') {
            value = value === '' ? '' : Number(parseFloat(value).toFixed(2));
        }
        newItems[index] = { ...newItems[index], [field]: value };
        setFormData({ ...formData, items: newItems });
    };

    const addItem = () => {
        setFormData({
            ...formData,
            items: [...items, { spare_part_id: '', quantity: '', price: '' }]
        });
    };

    const removeItem = (index) => {
        if (items.length > 1) {
            const newItems = items.filter((_, i) => i !== index);
            setFormData({ ...formData, items: newItems });
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!selectedSupplier) newErrors.supplier_id = 'Ta\'minotchi tanlash majburiy';
        if (!firmId) newErrors.firm_id = 'Firma tanlanmagan';
        if (!selectedWarehouse) newErrors.warehouse_id = 'Ombor tanlash majburiy';
        if (!date) {
            newErrors.date = 'Sana tanlash majburiy';
        } else if (!isDirectorOrSuperAdmin) {
            // Check if date is within last 2 days for non-directors
            const selectedDate = new Date(date);
            const twoDaysAgo = new Date(Date.now() - 2 * 24 * 60 * 60 * 1000);
            const today = new Date();
            
            if (selectedDate < twoDaysAgo || selectedDate > today) {
                newErrors.date = 'Faqat oxirgi 2 kun ichidagi sanalarni tanlashingiz mumkin';
            }
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const onSubmit = e => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        // Convert all quantities and prices to numbers with 2 decimal places
        const processedItems = items.map(item => ({
            ...item,
            quantity: Number(parseFloat(item.quantity || 0).toFixed(2)),
            price: Number(parseFloat(item.price || 0).toFixed(2))
        }));
        createSparepartImport({
            ...formData,
            items: processedItems,
            supplier_id: selectedSupplier?.value,
            firm_id: firmId || formData.firm_id,
            warehouse_id: selectedWarehouse?.value,
            date: date.toISOString().split('T')[0]
        });
        handleClose();
        setFormData({
            supplier_id: '',
            firm_id: '',
            warehouse_id: '',
            date: new Date(),
            description: '',
            items: [{ spare_part_id: '', quantity: '', price: '' }]
        });
        setSelectedSupplier(null);
        setSelectedWarehouse(null);
    };

    const sparePartOptions = spare_parts?.map(sparePart => ({
        value: sparePart.id,
        label: sparePart.name,
        unit: sparePart.unit
    })) || [];

    const supplierOptions = suppliers?.map(supplier => ({
        value: supplier.id,
        label: supplier.name
    })) || [];


    // Don't filter warehouses on frontend, just map what we get from the API
    const warehouseOptions = warehouses.map(warehouse => ({
        value: warehouse.id,
        label: warehouse.name
    }));


    const handleWarehouseChange = (selected) => {
        setSelectedWarehouse(selected);
        
        // Completely reset items when warehouse changes
        setFormData({
            ...formData,
            items: [{ spare_part_id: '', quantity: '', price: '' }]
        });
        
        // Generate a new key to force re-render of the form items
        setFormKey(Date.now());
    };

    return (
        <Modal show={show} onHide={handleClose} size="xl">
            <Modal.Header closeButton>
                <Modal.Title>Ehtiyot qism importini qo'shish</Modal.Title>
            </Modal.Header>
            <Form onSubmit={onSubmit}>
                <Modal.Body>
                    <div className="row">
                        <div className="col-md-3">
                            <Form.Group className="mb-3">
                                <Form.Label>Ta'minotchi</Form.Label>
                                <Select
                                    value={selectedSupplier}
                                    onChange={setSelectedSupplier}
                                    options={supplierOptions}
                                    placeholder="Ta'minotchini tanlang..."
                                    isSearchable={true}
                                />
                            </Form.Group>
                        </div>
                        <div className="col-md-3">
                            <Form.Group className="mb-3">
                                <Form.Label>Firma</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={firmName || ''}
                                    readOnly
                                    disabled
                                    style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                                />
                                <small className="text-muted">Firma global firm switcher orqali tanlangan</small>
                            </Form.Group>
                        </div>
                        <div className="col-md-3">
                            <Form.Group className="mb-3">
                                <Form.Label>Ombor</Form.Label>
                                <Select
                                    value={selectedWarehouse}
                                    onChange={handleWarehouseChange}
                                    options={warehouseOptions}
                                    placeholder="Omborni tanlang..."
                                    isSearchable={true}
                                    isDisabled={!firmId}
                                />
                                {firmId && warehouseOptions.length === 0 && (
                                    <small className="text-danger">
                                        Bu firma uchun ehtiyot qism tipdagi omborlar mavjud emas
                                    </small>
                                )}
                            </Form.Group>
                        </div>
                        <div className="col-md-3">
                            <Form.Group className="mb-3">
                                <Form.Label>Sana <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="date"
                                    name="date"
                                    value={date ? date.toISOString().split('T')[0] : ''}
                                    onChange={(e) => setFormData({ ...formData, date: new Date(e.target.value) })}
                                    max={new Date().toISOString().split('T')[0]}
                                    min={isDirectorOrSuperAdmin ? undefined : new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                                />
                                {errors.date && <div className="text-danger small">{errors.date}</div>}
                                {!isDirectorOrSuperAdmin && (
                                    <small className="text-muted">
                                        Faqat oxirgi 2 kun ichidagi sanalarni tanlashingiz mumkin
                                    </small>
                                )}
                            </Form.Group>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-12">
                            <Form.Group className="mb-3">
                                <Form.Label>Transport raqami</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="description"
                                    value={description}
                                    onChange={onChange}
                                    placeholder="Transport raqamini kiriting..."
                                />
                            </Form.Group>
                        </div>
                    </div>

                    <div className="border rounded p-3 mb-3" key={formKey}>
                        <h5 className="mb-3">Ehtiyot qismlar</h5>
                        {items.map((item, index) => (
                            <SparepartImportItemForm
                                key={`${formKey}-${index}`}
                                item={item}
                                index={index}
                                sparePartOptions={sparePartOptions}
                                onChange={onItemChange}
                                onRemove={removeItem}
                            />
                        ))}
                        <div className="d-flex justify-content-center">
                            <Button 
                                variant="outline-primary" 
                                onClick={addItem}
                                className="d-flex align-items-center"
                            >
                                <Plus size={16} className="me-2" />
                                Ehtiyot qism qo'shish
                            </Button>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        Bekor qilish
                    </Button>
                    <Button variant="primary" type="submit" disabled={!selectedSupplier || !firmId || !selectedWarehouse}>
                        Saqlash
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

SparepartImportCreateForm.propTypes = {
    show: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    createSparepartImport: PropTypes.func.isRequired,
    getSupplierOptions: PropTypes.func.isRequired,
    getWarehouseOptions: PropTypes.func.isRequired,
    getSparepartOptions: PropTypes.func.isRequired,
    spare_parts: PropTypes.array,
    suppliers: PropTypes.array,
    warehouses: PropTypes.array,
    user: PropTypes.object
};

const mapStateToProps = state => ({
    spare_parts: state.spare_part.spare_part_options,
    suppliers: state.material.supplier_options,
    warehouses: state.spare_part.warehouse_options,
    user: state.auth.user
});

export default connect(mapStateToProps, { 
    createSparepartImport, 
    getSupplierOptions,
    getWarehouseOptions,
    getSparepartOptions
})(SparepartImportCreateForm); 